import { Kbd as _Kbd, Button, chakra, Heading, Icon, IconProps, RTooltip, Tag, Text, VStack } from '@ramp/components';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ROUTES from 'router/routes';

const Kbd = chakra(_Kbd, {
  baseStyle: {
    position: 'relative',
    top: '-2px'
  }
});

// Inline copies of the @ramp/icons and lucide icons: the page is in the entry bundle, those packages are not.
const strokeIcon = (paths: string[]) => (props: IconProps) => (
  <Icon
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {paths.map(d => (
      <path key={d} d={d} />
    ))}
  </Icon>
);

const Command = strokeIcon(['M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3']);
const Option = strokeIcon(['M3 3h6l6 18h6', 'M14 3h7']);
const RefreshCcw = strokeIcon([
  'M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8',
  'M3 3v5h5',
  'M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16',
  'M16 16h5v5'
]);
const CircleQuestionMark = strokeIcon([
  'M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0',
  'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3',
  'M12 17h.01'
]);

const Server = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    viewBox="0 0 64 64"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={5}
  >
    <path
      stroke="#4A4A4A"
      className="strokeBackArea"
      d="M20.058 7.077h33.75A5.192 5.192 0 0 1 59 12.269v10.385a5.192 5.192 0 0 1-5.192 5.192H40.827m-12.981 0L8.375 8.376c-1.298-1.299-1.298 0-1.298 6.49v7.788a5.192 5.192 0 0 0 5.192 5.192h15.577ZM59 46.02v-2.597a5.193 5.193 0 0 0-5.192-5.192H51.21m-38.94-.001a5.192 5.192 0 0 0-5.193 5.193v10.385A5.193 5.193 0 0 0 12.269 59h42.837l2.596-1.298L59 59 38.23 38.23H12.27Zm5.191 10.385h.025M7.077 7.077 59 59"
    />
    <path
      stroke="#fff"
      className="strokeFrontArea"
      d="M17.98 5h33.75a5.192 5.192 0 0 1 5.193 5.192v10.385a5.192 5.192 0 0 1-5.192 5.192H38.75m-12.98.001L6.297 6.297C5 5 5 6.298 5 12.788v7.789a5.192 5.192 0 0 0 5.192 5.192H25.77Zm31.153 18.172v-2.596a5.192 5.192 0 0 0-5.192-5.192h-2.596m-38.943 0A5.193 5.193 0 0 0 5 41.346v10.385a5.192 5.192 0 0 0 5.192 5.192H53.03l2.596-1.298 1.298 1.298-20.77-20.77h-25.96Z"
    />
    <path stroke="#D60928" d="M15.384 46.538h.025M5 5l51.923 51.923" />
  </svg>
);

const SomethingWentWrong = () => {
  const [spin, setSpin] = React.useState(false);

  const navigateToHome = () => {
    setSpin(true);
    setTimeout(() => {
      window.location.href = ROUTES.BASE.ROOT;
    }, 500);
  };

  const { t } = useTranslation();

  return (
    <VStack w="full" h="100vh" align="center" justify="center" spacing={6}>
      <VStack spacing={4}>
        <Server size={164} />
        <VStack gap={2}>
          <Heading>{t('components.errorPage.title')}</Heading>
          <Text mt={0.5} textAlign="center">
            <chakra.b>{t('components.errorPage.stepOne.text')}</chakra.b>
            <br />
            <RTooltip
              tooltipProps={{
                p: 2
              }}
              tooltip={
                <VStack gap={2} alignItems="stretch">
                  <chakra.span
                    display="flex"
                    justifyContent="space-between"
                    p={2}
                    gap={6}
                    _light={{
                      bg: 'blackAlpha.100'
                    }}
                    _dark={{
                      bg: 'blackAlpha.400'
                    }}
                    rounded="md"
                  >
                    <Tag mr={2} size="sm" colorScheme="brand">
                      macOS
                    </Tag>
                    <VStack alignItems="end" as={chakra.span}>
                      <chakra.span>
                        <Kbd>
                          <Icon position="relative" top="3px" as={Command} />
                        </Kbd>
                        + <Kbd>Shift</Kbd> + <Kbd>R</Kbd>
                      </chakra.span>
                    </VStack>
                  </chakra.span>
                  <chakra.span
                    display="flex"
                    justifyContent="space-between"
                    p={2}
                    gap={6}
                    _light={{
                      bg: 'blackAlpha.100'
                    }}
                    _dark={{
                      bg: 'blackAlpha.400'
                    }}
                    rounded="md"
                  >
                    <Tag mr={2} size="sm" colorScheme="brand">
                      macOS (Safari)
                    </Tag>
                    <VStack alignItems="end" as={chakra.span}>
                      <chakra.span>
                        <Kbd>
                          <Icon position="relative" top="3px" as={Command} />
                        </Kbd>
                        +
                        <Kbd>
                          <Icon position="relative" top="3px" as={Option} />
                        </Kbd>
                        +<Kbd>R</Kbd>
                      </chakra.span>
                    </VStack>
                  </chakra.span>
                  <chakra.span
                    display="flex"
                    justifyContent="space-between"
                    p={2}
                    gap={6}
                    _light={{
                      bg: 'blackAlpha.100'
                    }}
                    _dark={{
                      bg: 'blackAlpha.400'
                    }}
                    rounded="md"
                  >
                    <Tag mr={2} size="sm" colorScheme="brand">
                      {t('components.errorPage.stepOne.otherOs')}
                    </Tag>
                    <chakra.span>
                      <Kbd>Ctrl</Kbd> + <Kbd>Shift</Kbd> + <Kbd>R</Kbd>
                    </chakra.span>
                  </chakra.span>
                </VStack>
              }
            >
              <chakra.span display="inline-flex" alignItems="center" gap={1} color="brand.400">
                <Icon boxSize="1.4em" as={CircleQuestionMark} /> {t('components.errorPage.stepOne.reload')}
              </chakra.span>
            </RTooltip>
          </Text>
          <Text textAlign="center">
            <Trans i18nKey="components.errorPage.stepTwo.text" />
          </Text>
        </VStack>
      </VStack>
      <Button
        w="200px"
        variant="brand"
        onClick={navigateToHome}
        leftIcon={
          <RefreshCcw
            boxSize="16px"
            style={spin ? { transition: 'all 0.5s ease-in-out', transform: 'rotate(360deg)' } : undefined}
          />
        }
      >
        {t('components.errorPage.reloadPage')}
      </Button>
    </VStack>
  );
};

export default SomethingWentWrong;
